import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Card, Typography, Link as MuiLink } from '@material-ui/core'
import * as Yup from 'yup'
import PasswordField from 'components/PasswordField'
import ProgressButton from 'components/ProgressButton'
import { colors } from 'utils/colors'

// Shared by the public activation-link flow (SetPassword.js) and the forced
// in-app gate (ForcePasswordChange.js) — same schema either way.
export const setPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => {
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
      }
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required')
})

// Presentational "set a new password" card used by both the public
// activation-link page and the authenticated forced-change gate. Each caller
// owns its own useFormik/onSubmit (the two flows submit to different backend
// modes) and passes the result in — this component only renders the form.
const SetNewPasswordCard = ({
  email,
  formik,
  submitting,
  onBack,
  backLabel = 'Back to Login',
  backDisabled = false
}) => {
  const classes = useStyles()

  return (
    <Box className={classes.container}>
      <Card className={classes.card}>
        <Typography variant="h5" className={classes.header}>
          Set a New Password
        </Typography>
        {email && (
          <Typography variant="body2" align="center" className={classes.subHeader}>
            Create a password for <strong>{email}</strong>
          </Typography>
        )}

        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <PasswordField label="New Password" name="password" formik={formik} />
          <PasswordField label="Confirm New Password" name="confirmPassword" formik={formik} />

          <ProgressButton
            type="submit"
            className={classes.submitButton}
            label="Set Password"
            inProgress={submitting}
            disabled={submitting || backDisabled || !formik.isValid}
          />
        </form>

        <Box mt={1.5} textAlign="center">
          <MuiLink
            component="button"
            type="button"
            onClick={onBack}
            className={classes.backLink}
            style={
              submitting || backDisabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}
            }>
            {backLabel}
          </MuiLink>
        </Box>
      </Card>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    padding: '20px'
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    borderRadius: '16px',
    padding: '35px 40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: colors.indigo
  },
  subHeader: {
    marginBottom: '24px',
    color: '#64748b'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  submitButton: {
    width: '100%',
    marginTop: '8px',
    padding: '12px',
    height: '48px',
    borderRadius: '8px',
    fontWeight: '600'
  },
  backLink: {
    cursor: 'pointer',
    color: colors.indigo,
    fontWeight: 600,
    fontSize: '0.875rem',
    background: 'none',
    border: 'none'
  }
})

export default SetNewPasswordCard
